import React, { useState } from 'react';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { Button } from '@h2/ui';
import { Label } from '@h2/ui';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@h2/ui';
import {
  FileText,
  Search,
  Globe,
  CheckCircle,
  User,
  Video,
  Settings,
  Edit,
  Folder,
  Calendar,
  Clock,
  Star,
  Target,
  Zap,
  Code,
  Database,
  Monitor,
  Smartphone,
  Headphones,
  Mic,
  Camera,
  Image,
  Film,
  Music,
  Volume2,
  Mail,
  Phone,
  MessageSquare,
  Send,
  Download,
  Upload,
  Save,
  Copy,
  Trash2,
  Archive,
  Filter,
  Layout,
  Grid,
  List,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Heart,
  ThumbsUp,
  Award,
  Flag,
  Bookmark,
  Share,
  ExternalLink,
  Link,
  Paperclip,
  Scissors,
  Printer,
  Scan
} from 'lucide-react';

// 사용 가능한 아이콘들
const availableIcons = {
  // 기본 작업 아이콘들
  FileText,
  Search,
  Globe,
  CheckCircle,
  User,
  Video,
  Settings,
  Edit,
  
  // 일반 아이콘들
  Folder,
  Calendar,
  Clock,
  Star,
  Target,
  Zap,
  Code,
  Database,
  Monitor,
  Smartphone,
  
  // 미디어 아이콘들
  Headphones,
  Mic,
  Camera,
  Image,
  Film,
  Music,
  Volume2,
  
  // 커뮤니케이션 아이콘들
  Mail,
  Phone,
  MessageSquare,
  Send,
  
  // 파일 액션 아이콘들
  Download,
  Upload,
  Save,
  Copy,
  Trash2,
  Archive,
  
  // UI 아이콘들
  Filter,
  Layout,
  Grid,
  List,
  Eye,
  EyeOff,
  
  // 보안 아이콘들
  Lock,
  Unlock,
  Shield,
  
  // 상태 아이콘들
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  
  // 소셜 아이콘들
  Heart,
  ThumbsUp,
  Award,
  Flag,
  Bookmark,
  Share,
  
  // 연결 아이콘들
  ExternalLink,
  Link,
  Paperclip,
  
  // 도구 아이콘들
  Scissors,
  Printer,
  Scan
};

// 모든 사용 가능한 아이콘들을 배열로 정의
const allAvailableIcons = [
  // 기본 작업
  'FileText', 'Search', 'Globe', 'CheckCircle', 'User', 'Video', 'Settings', 'Edit',
  // 일반
  'Folder', 'Calendar', 'Clock', 'Star', 'Target', 'Zap', 'Code', 'Database', 'Monitor', 'Smartphone',
  // 미디어
  'Headphones', 'Mic', 'Camera', 'Image', 'Film', 'Music', 'Volume2',
  // 커뮤니케이션
  'Mail', 'Phone', 'MessageSquare', 'Send',
  // 파일
  'Download', 'Upload', 'Save', 'Copy', 'Trash2', 'Archive',
  // UI
  'Filter', 'Layout', 'Grid', 'List', 'Eye', 'EyeOff',
  // 보안
  'Lock', 'Unlock', 'Shield',
  // 상태
  'AlertTriangle', 'CheckCircle2', 'XCircle', 'Info',
  // 소셜
  'Heart', 'ThumbsUp', 'Award', 'Flag', 'Bookmark', 'Share',
  // 연결
  'ExternalLink', 'Link', 'Paperclip',
  // 도구
  'Scissors', 'Printer', 'Scan'
];

// 아이콘 렌더링 컴포넌트
const TaskTypeIcon: React.FC<{ iconName: string; className?: string }> = ({ iconName, className = "h-4 w-4" }) => {
  const IconComponent = availableIcons[iconName as keyof typeof availableIcons];
  return IconComponent ? <IconComponent className={className} /> : <Settings className={className} />;
};

export const TaskTypeIconPicker: React.FC = () => {
  const { formData, updateField } = useConfigurationStore();
  const [isOpen, setIsOpen] = useState(false);
  
  const currentIcon = (formData.icon as string) || 'Settings';
  
  // 모든 아이콘 목록 사용
  const allIcons = allAvailableIcons;
  
  const handleIconSelect = (iconName: string) => {
    updateField('icon', iconName);
    setIsOpen(false);
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">아이콘</Label>
      <div>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              className="h-10 w-16 p-0"
              type="button"
            >
              <TaskTypeIcon iconName={currentIcon} className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="start">
            <div className="grid grid-cols-8 gap-2 max-h-60 overflow-y-auto">
              {allIcons.map((iconName) => (
                <button
                  key={iconName}
                  onClick={() => handleIconSelect(iconName)}
                  className={`p-2 rounded transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    currentIcon === iconName 
                      ? 'bg-blue-100 dark:bg-blue-900 ring-2 ring-blue-500' 
                      : ''
                  }`}
                  title={iconName}
                >
                  <TaskTypeIcon iconName={iconName} className="h-4 w-4 mx-auto" />
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
